import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { environment, isDevelopment, shouldBypassPermissions } from '../api/environment';
import { getTokenTimeUntilExpiration } from '../utils/jwt.utils';
import type { AuthenticatedUser } from '@/features/auth/types/authenticated-user.type';
import {
  isUserTokenValid,
  userHasAllPermissions,
  userHasAnyPermission,
  userHasPermission,
  userHasRole,
} from '@/features/auth/utils/authenticated-user.utils';
import { partializeUser } from '@/shared/utils/partialize-user';
import { reconstructUser, type StoredUser } from '@/shared/utils/reconstruct-user.utils';

interface AuthState {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  errorLogout: string | null;
  // Nuevo: flag para saber si ya se hidrato
  hasHydrated: boolean;
  errorDate?: Date | null;
  tokenExpirationTimeout?: NodeJS.Timeout;
}

interface AuthActions {
  login: (user: AuthenticatedUser) => void;
  logout: () => void;
  updateUser: (user: AuthenticatedUser) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  // Nuevo: método para marcar como hidratado
  setHasHydrated: (state: boolean) => void;
  // Métodos de autorización
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  getToken: () => string | null;
  isTokenValid: () => boolean;
  setErrorLogout: (error: string | null) => void;
  // Nuevo: método para validar y limpiar estado si es necesario
  validateAndCleanup: () => void;
  startTokenTimer: (user: AuthenticatedUser) => void;
  stopTokenTimer: () => void;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    persist(
      (set, get) => ({
        // State
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        hasHydrated: false,
        errorLogout: null,
        tokenExpirationTimeout: undefined,
        // Basic Actions
        login: (user) => {
          set(
            {
              user,
              isAuthenticated: true,
              error: null,
            },
            false,
            'auth/login',
          );
          get().startTokenTimer(user);
        },
        logout: () =>
          set(
            {
              user: null,
              isAuthenticated: false,
              error: null,
            },
            false,
            'auth/logout',
          ),

        updateUser: (user) =>
          set(
            {
              user,
              isAuthenticated: true,
            },
            false,
            'auth/updateUser',
          ),

        setLoading: (loading) => set({ isLoading: loading }, false, 'auth/setLoading'),
        setError: (error) =>
          set({ error, errorDate: error ? new Date() : null }, false, 'auth/setError'),
        clearError: () => set({ error: null }, false, 'auth/clearError'),
        setHasHydrated: (state) => set({ hasHydrated: state }),

        validateAndCleanup: () => {
          const { user } = get();
          console.log('🔍 [AUTH STORE] validateAndCleanup called', {
            hasUser: !!user,
            userId: user?.userId,
          });

          if (user && !isUserTokenValid(user)) {
            console.warn('❌ [AUTH STORE] Token invalid during validation, logging out');
            get().logout();
            return;
          }

          if (user) {
            console.log('✅ [AUTH STORE] Starting token timer for valid user');
            get().startTokenTimer(user);
          }
        },

        // Authorization Methods
        hasPermission: (permission) => {
          if (shouldBypassPermissions() && isDevelopment()) {
            if (environment.enableLogging) {
              console.warn(`🚨 [DEV BYPASS] hasPermission(${permission}) - Bypassed`);
            }
            return true;
          }
          const { user } = get();
          return user ? userHasPermission(user, permission) : false;
        },

        hasAnyPermission: (permissions) => {
          if (shouldBypassPermissions() && isDevelopment()) {
            if (environment.enableLogging) {
              console.warn(
                `🚨 [DEV BYPASS] hasAnyPermission([${permissions.join(', ')}]) - Bypassed`,
              );
            }
            return true;
          }
          const { user } = get();
          return user ? userHasAnyPermission(user, permissions) : false;
        },

        hasAllPermissions: (permissions) => {
          if (shouldBypassPermissions() && isDevelopment()) {
            if (environment.enableLogging) {
              console.warn(
                `🚨 [DEV BYPASS] hasAllPermissions([${permissions.join(', ')}]) - Bypassed`,
              );
            }
            return true;
          }
          const { user } = get();
          return user ? userHasAllPermissions(user, permissions) : false;
        },

        hasRole: (role) => {
          if (shouldBypassPermissions() && isDevelopment()) {
            if (environment.enableLogging) {
              console.warn(`🚨 [DEV BYPASS] hasRole(${role}) - Bypassed`);
            }
            return true;
          }
          const { user } = get();
          return user ? userHasRole(user, role) : false;
        },

        getToken: () => {
          const { user } = get();
          return user ? user.accessToken : null;
        },

        isTokenValid: () => {
          const { user } = get();
          return user ? isUserTokenValid(user) : false;
        },
        setErrorLogout: (error) => {
          set({ errorLogout: error }, false, 'auth/setErrorLogout');
        },
        startTokenTimer: (user: AuthenticatedUser) => {
          const expireTime = getTokenTimeUntilExpiration({ value: user.accessToken });

          console.log('⏰ [AUTH STORE] Starting token timer', {
            expireTime,
            expireTimeInMinutes: expireTime / 1000 / 60,
            userId: user.userId,
          });

          // Stop any existing timer first
          get().stopTokenTimer();

          // If expireTime is 0 or negative, token is expired or about to expire
          if (expireTime <= 0) {
            console.warn('❌ [AUTH STORE] Token already expired or expiring, not setting timer');
            return;
          }

          const timeout = setTimeout(() => {
            console.warn('⏰ [AUTH STORE] Token timer expired, logging out');
            get().logout();
          }, expireTime);

          set({ tokenExpirationTimeout: timeout });
        },
        stopTokenTimer: () => {
          clearTimeout(get().tokenExpirationTimeout);
        },
      }),
      {
        name: 'auth-store',
        storage: createJSONStorage(() => localStorage),
        partialize: ({ user }): { user: StoredUser | null } => {
          return { user: partializeUser(user) };
        },
        merge: (persistedState, currentState) => {
          const persisted = persistedState as { user: StoredUser };

          console.log('🔄 [AUTH STORE] Starting merge/hydration', {
            hasPersistedState: !!persisted,
            hasPersistedUser: !!persisted?.user,
            persistedUserId: persisted?.user?.userId,
          });

          try {
            const reconstructedUser = persisted?.user ? reconstructUser(persisted.user) : null;

            const mergedState = {
              ...currentState,
              user: reconstructedUser,
              isAuthenticated: !!reconstructedUser,
              isLoading: false,
              error: null,
              hasHydrated: true,
            };

            console.log('✅ [AUTH STORE] Merge complete', {
              hasUser: !!mergedState.user,
              isAuthenticated: mergedState.isAuthenticated,
              userId: mergedState.user?.userId,
            });

            return mergedState;
          } catch (error) {
            console.error('❌ [AUTH STORE] Error during hydration:', error);
            return {
              ...currentState,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              hasHydrated: true,
            };
          }
        },
        onRehydrateStorage: () => (state) => {
          console.log('🔄 [AUTH STORE] onRehydrateStorage callback', {
            hasState: !!state,
            hasUser: !!state?.user,
            userId: state?.user?.userId,
          });

          if (state) {
            state.setHasHydrated(true);
            // Validar el estado después de la hidratación
            setTimeout(() => {
              console.log('⏰ [AUTH STORE] Running validateAndCleanup after hydration');
              state.validateAndCleanup();
            }, 0);
          }
        },
      },
    ),
    {
      name: 'auth-store',
      enabled: isDevelopment(),
    },
  ),
);
